import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { AlertFillIcon } from '@arcade/web/icons/react'
import {
  ArcadeAvatar,
  ArcadeBorderedCard,
  BaseCell,
  CheckboxCell,
  CheckboxInput,
  Flex,
  InteractiveCell,
  spacing,
  Stack,
  Text,
} from '@cash-design-system/react'
import { ArcadeAvatarFromAvatarProto, getArcadeAvatarFromAvatarProto } from '@cash-web/avatar-from-proto'
import { FormBlockerElementMerchantTransactionElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { useColorProtoAdapter } from '@cash-web/shared-proto-adapters-ui-color'
import { Currency, moneyFormatter } from '@cash-web/shared-util-formatting'

const CellCard = styled(ArcadeBorderedCard)`
  overflow: hidden;
`
export const MerchantTransactionElementContainer = styled.div<{
  isSelected: boolean
  selectable: boolean
  isRadioOption: boolean
}>`
  border-radius: 1rem;
  display: grid;
  grid-template-columns: 1fr ${props => (props.isRadioOption ? '1.25rem' : '2.75rem')};
  padding: ${({ theme }) => theme.spacing.m};
  border: 0.125rem solid ${props => props.theme.colors.formFieldBackground};
  width: 100%;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.arcadeColors.semantic.textSubtle};
  text-align: left;

  svg > path {
    color: ${props => props.theme.colors.error};
  }
  ${props =>
    props.isSelected &&
    css`
      border-color: ${props.theme.colors.primary};
    `}
  ${props =>
    props.selectable &&
    css`
      cursor: pointer;
    `}

  font: inherit;
  outline: inherit;
  user-select: none;
  ${({ theme }) => theme.selectors.keyboard} &:focus {
    border-radius: 0.5rem;
    outline: 0.5rem solid ${props => props.theme.colors.primary}33;
  }
`
export const ServerColorText = styled(Text)<{ serverColor?: string }>`
  ${props => props.serverColor && `color: ${props.serverColor};`};
`

const MoneyLabel = styled(Text)<{ strikethrough?: boolean }>`
  text-decoration: ${({ strikethrough }) => (strikethrough ? 'line-through' : undefined)};
`

export const MerchantTransactionLabel = ({
  isRadioOption,
  merchantName,
  date,
  transactionStatus,
  locationLineOne,
  locationLineTwo,
  avatar,
  avatarBadged,
  amount,
  transactionStatusColor,
}: FormBlockerElementMerchantTransactionElement & { isRadioOption: boolean }) => {
  const { getColorValue } = useColorProtoAdapter()
  const arcadeAvatarProps = avatar ? getArcadeAvatarFromAvatarProto({ avatarProto: avatar }) : {}
  return (
    <Flex gap={spacing.m}>
      {avatar && (
        <ArcadeAvatar
          {...arcadeAvatarProps}
          size="small"
          {...(avatarBadged ? { overlayIcon: { element: <AlertFillIcon size={24} variant="danger" /> } } : {})}
        />
      )}
      <Stack space={spacing.xxs}>
        <Text typography="labelMedium">{merchantName}</Text>
        <Text typography="labelMedium" color="textSubtle">
          {date}
        </Text>
        {isRadioOption && (
          <Text typography="labelMedium" color="textSubtle">
            {moneyFormatter().formatMoney(amount?.money?.amount)}
          </Text>
        )}
        <Text typography="labelMedium" color="textSubtle">
          {locationLineOne}
        </Text>
        <Text typography="labelMedium" color="textSubtle">
          {locationLineTwo}
        </Text>
        {!isRadioOption && (
          <ServerColorText typography="labelMedium" serverColor={getColorValue(transactionStatusColor)}>
            {transactionStatus}
          </ServerColorText>
        )}
      </Stack>
    </Flex>
  )
}
export type MerchantTransactionElementProps = FormBlockerElementMerchantTransactionElement & {
  selected?: boolean
  index?: number
  onChange?: (id?: string) => void
}
export const MerchantTransactionElement = ({
  onChange,
  selected,
  index,
  ...props
}: MerchantTransactionElementProps) => {
  const flow = useFlowContext()
  const isRadioOption = typeof onChange === 'function' && props.selectable === true
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (isArcadeMigrationEnabled) {
    const formattedMoneyLabel = props.amount && (
      // we only need to support strikethrough markdown to keep parity with mobile
      <MoneyLabel typography="labelSmall" color="textStandard" strikethrough={props.amount?.markdown === '~~'}>
        {moneyFormatter().formatMoney(
          props.amount?.money?.amount,
          props.amount?.money?.currency_code
            ? Currency.Companion.fromISOTextCode(props.amount.money.currency_code)
            : undefined
        )}
      </MoneyLabel>
    )
    const cellProps = {
      asset: {
        avatar: (
          <ArcadeAvatarFromAvatarProto
            size="small"
            avatarProto={props.avatar}
            {...(props.avatarBadged ? { overlayIcon: { arcadeId: 'tRJa1k' } } : undefined)}
          />
        ),
      },
      body: (
        <Flex flexDirection="column">
          {props.locationLineOne && <Text>{props.locationLineOne}</Text>}
          {props.date && <Text>{props.date}</Text>}
          {props.locationLineTwo && <Text>{props.locationLineTwo}</Text>}
          {props.transactionStatus && <Text color="textSubtle">{props.transactionStatus}</Text>}
        </Flex>
      ),
    }

    if (props.actions && props.actions.length > 0)
      return (
        <CellCard padding="m">
          <InteractiveCell
            label={props.merchantName ?? ''}
            cellProps={{
              accessory: formattedMoneyLabel,
              ...cellProps,
            }}
            onClick={() => {
              props.actions?.forEach(action => {
                flow.executeBlockerAction(action)
              })
            }}
          />
        </CellCard>
      )
    if (props.selectable)
      return (
        <CheckboxCell
          label={props.merchantName ?? ''}
          cellProps={cellProps}
          checked={selected === true}
          onChange={() => {
            if (isRadioOption) {
              onChange?.(props.transactionId)
            }
          }}
          accessoryLabel={formattedMoneyLabel}
        />
      )
    return (
      <BaseCell
        label={props.merchantName ?? ''}
        asset={cellProps.asset}
        body={cellProps.body}
        accessory={formattedMoneyLabel}
      />
    )
  }
  return (
    <MerchantTransactionElementContainer
      as={isRadioOption ? 'label' : 'button'}
      data-testid={`merchant-transaction-element-${props.transactionId}`}
      isRadioOption={isRadioOption}
      selectable={props.selectable === true}
      isSelected={selected === true}
      onClick={() => {
        if (!isRadioOption && props.actions && props.actions.length > 0) {
          props.actions.forEach(action => {
            flow.executeBlockerAction(action)
          })
        }
      }}
    >
      <MerchantTransactionLabel {...props} isRadioOption={isRadioOption} />
      {isRadioOption ? (
        <CheckboxInput
          variant="primary"
          checked={selected}
          label={null}
          onChange={() => {
            if (isRadioOption) {
              onChange?.(props.transactionId)
            }
          }}
        />
      ) : (
        <Text typography="labelMedium">{moneyFormatter().formatMoney(props.amount?.money?.amount)}</Text>
      )}
    </MerchantTransactionElementContainer>
  )
}
